/**
 * SOW token provenance
 * ────────────────────
 * Classifies a `{{protoss:<path>}}` binding-token path as DATA-DRIVEN (its resolved
 * value comes from deal / estimate / resource-plan data, or a declarative
 * calculation) vs STATIC (workspace brand, system dates, doc metadata, config,
 * assets). The "generated content" highlight marks only the data-driven ones, so
 * users can verify what was auto-filled/computed without static boilerplate noise.
 *
 * Lives in `@protoss/doc` (not `@protoss/shared`) because every consumer - the web
 * view, the docx renderer, and the Google-Docs renderer - already depends on
 * `@protoss/doc`. Kept deliberately separate from `sowTokenValidity.ts`
 * (resolvable-vs-unbound) and the editor's `inferTokenSource` (chip colour), which
 * answer different questions.
 */
/** Prefixes whose resolved value is dynamic deal/estimate/RP data or a computed value. */
const DATA_DRIVEN_PREFIXES = [
    'context.', // questionnaire field answers (client name, dates, ...)
    'item.', // dynamicBlock row scope (data-table cells)
    'data.',
    'totals.',
    'staffing',
    'partners',
    'discount',
    'paymentSchedule',
    'calc.', // declarative calculation outputs (dates, percents, amounts)
    'fillable.', // unbound, user-fillable placeholders (no data binding; filled via token override)
];
/** Prefixes that are static / workspace constants - never highlighted. */
const STATIC_PREFIXES = [
    'brand.',
    'date.',
    'meta.',
    'sow.',
    'config.',
    'asset:',
];
/**
 * True when a token path resolves to dynamically-generated/computed content (so it
 * should be highlighted). The optional `protoss:` namespace is tolerated. Static
 * namespaces win (so an unexpected overlap never highlights brand/date/config).
 */
export function isDataDrivenSowToken(path) {
    const p = path.replace(/^protoss:/, '').trim();
    if (!p)
        return false;
    if (STATIC_PREFIXES.some((s) => p.startsWith(s)))
        return false;
    return DATA_DRIVEN_PREFIXES.some((d) => p.startsWith(d));
}
// ── Fillable (unbound) tokens ───────────────────────────────────────────────────
// A `{{protoss:fillable.<key>}}` token has NO automatic data binding. It renders a visible
// placeholder until the user fills it (double-click in the SOW editor -> persisted as a
// `fillable.<key>` entry in deal_sow.token_overrides, like context/calc overrides). Used for
// standard clauses with a deal-specific blank (e.g. the travel expense budget amount).
export const FILLABLE_PREFIX = 'fillable.';
/** Per-key default placeholder for an UNFILLED fillable token; falls back to a blank line. */
const FILLABLE_PLACEHOLDERS = {
    // Remote engagement assumes no travel, so its estimate defaults to $0; onsite is a fill-in.
    // Distinct keys (not a shared `travel_expense_budget`) so editing one does NOT sync the other.
    travel_expense_budget_remote: '$0',
    travel_expense_budget_onsite: '$XX,XXX',
    // Sponsor names are agreed on the call, not captured in the questionnaire (no client_sponsor
    // field exists), so this is typed straight into the SOW editor.
    client_sponsor: '[Client sponsor name(s)]',
    // What the fixed fee buys, in the client's words (e.g. "the 3 (three) four-hour sessions and
    // related solutioning and documentation"). Deal-specific prose, so it is filled per SOW.
    fixed_fee_deliverable: '[the agreed scope of work]',
};
/** The visible placeholder shown for an unfilled fillable token (so the user can find + fill it). */
export function fillablePlaceholder(key) {
    const k = key.startsWith(FILLABLE_PREFIX) ? key.slice(FILLABLE_PREFIX.length) : key;
    return FILLABLE_PLACEHOLDERS[k] ?? '________';
}
/**
 * Resolve a `fillable.<key>` token to its display string: the user's entered value when present,
 * otherwise the visible placeholder. `filled` is the per-key fillable-override map.
 */
export function resolveFillableToken(path, filled) {
    const key = path.startsWith(FILLABLE_PREFIX) ? path.slice(FILLABLE_PREFIX.length) : path;
    const v = filled?.[key];
    return typeof v === 'string' && v.trim() ? v : fillablePlaceholder(key);
}
/**
 * Resolve `{{protoss:fillable.<key>}}` tokens EMBEDDED IN RESOLVED DATA-TABLE VALUES (a contract
 * may bind a cell to a fillable, e.g. the RCP Client Sponsor(s) row). The renderers substitute
 * tokens in a single pass, so a token that only surfaces after `item.<col>` resolution would
 * otherwise reach the document as raw text. Applied once at context-assembly time (the deal-sow
 * chokepoint), which fixes View, editor sample, docx, and Google alike. Non-string values and
 * rows without a fillable token pass through untouched (same references, no copy).
 */
export function resolveFillableTokensInData(rows, filled) {
    const TOKEN = /\{\{protoss:(fillable\.[^}]+)\}\}/g;
    let rowsChanged = false;
    const out = rows.map((row) => {
        let rowChanged = false;
        const next = { ...row };
        for (const [col, value] of Object.entries(row)) {
            if (typeof value !== 'string' || !value.includes(`{{protoss:${FILLABLE_PREFIX}`))
                continue;
            next[col] = value.replace(TOKEN, (_m, path) => resolveFillableToken(path, filled));
            rowChanged = true;
        }
        if (!rowChanged)
            return row;
        rowsChanged = true;
        return next;
    });
    return rowsChanged ? out : rows;
}
//# sourceMappingURL=sow-token-provenance.js.map